import React, { useState, useEffect, useMemo } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { useQueryClient } from 'react-query';
import { useAuth } from '../context/AuthContext';
import { usePermissions } from '../context/PermissionsContext';
import { PageChromeProvider, usePageChrome } from '../context/PageChromeContext';
import { AtsMatchRunProvider, useAtsMatchRun } from '../context/AtsMatchRunContext';
import {
  FiMenu,
  FiX,
  FiHome,
  FiBriefcase,
  FiFileText,
  FiUsers,
  FiClock,
  FiDatabase,
  FiLayers,
  FiGlobe,
  FiMail,
  FiEye,
  FiBarChart2,
  FiZap,
  FiLifeBuoy,
  FiChevronsLeft,
  FiChevronsRight,
} from 'react-icons/fi';
import { APP_UI_VERSION } from '../version';
import { PRODUCT_DISPLAY_NAME } from '../config/product';
import NotificationBell from './NotificationBell';
import BrandLogo from './BrandLogo';
import UserMenu from './UserMenu';
import { PageTransition, MotionSidebarItem } from './motion';
import { useProductAnalyticsTracking } from '../hooks/useProductAnalytics';
import { titleFromPath } from '../utils/pageTitles';
import { resolveActiveMenuPath } from '../utils/sidebarActivePath';
import useAutoHideScrollbar from '../hooks/useAutoHideScrollbar';
import './Layout.css';
import { appAlert } from '../utils/appDialog';
import AiMatchProcessingBar from './AiMatchProcessingBar';
import './AiMatchProcessingBar.css';

const SIDEBAR_COLLAPSED_KEY = 'gobunnyy-sidebar-collapsed';

function displayName(u) {
  const name = [u?.first_name, u?.last_name].filter(Boolean).join(' ').trim();
  return name || u?.email || 'user';
}

// Workspace IA (WP0): labels/grouping only — URLs and permissions unchanged.
const MENU_ITEMS = [
  { path: '/', label: 'Dashboard', icon: FiHome, tone: 'blue', roles: ['candidate', 'consultant', 'admin'], permission: 'tab_dashboard', section: 'Recruiting' },
  { path: '/jobs', label: 'Jobs', icon: FiBriefcase, tone: 'indigo', roles: ['candidate', 'consultant', 'admin'], permission: 'tab_jobs', section: 'Recruiting' },
  { path: '/ai-studio', label: 'AI Studio', icon: FiZap, tone: 'purple', roles: ['candidate', 'admin'], permission: 'tab_resumes', matchPaths: ['/ai-studio', '/resumes', '/linkedin-studio'], section: 'Recruiting' },
  { path: '/matches', label: 'ATS match', icon: FiFileText, tone: 'pink', roles: ['candidate', 'consultant', 'admin'], permission: 'tab_matches', section: 'Recruiting' },
  { path: '/candidates', label: 'Candidates', icon: FiUsers, tone: 'teal', roles: ['consultant', 'admin'], permission: 'tab_candidates', section: 'Recruiting' },
  { path: '/crm', label: 'Interactions', icon: FiDatabase, tone: 'orange', roles: ['candidate', 'consultant', 'admin'], permission: 'tab_crm', section: 'Recruiting' },
  { path: '/timesheets', label: 'Timesheets', icon: FiClock, tone: 'yellow', roles: ['consultant', 'admin'], permission: 'tab_timesheets', section: 'Recruiting' },
  { path: '/support', label: 'Support', icon: FiLifeBuoy, tone: 'pink', roles: ['consultant', 'admin', 'ticket_logger', 'external_ticket_logger'], permission: 'tab_support', section: 'Support' },
  { path: '/metadata', label: 'Metadata', icon: FiLayers, tone: 'indigo', roles: ['admin'], permission: 'tab_metadata', section: 'Admin' },
  { path: '/job-scanner', label: 'Job scanner', icon: FiGlobe, tone: 'cyan', roles: ['admin'], permission: 'tab_job_scanner', section: 'Admin' },
  { path: '/email-queue', label: 'Email queue', icon: FiMail, tone: 'blue', roles: ['admin'], permission: 'tab_email_queue', section: 'Admin' },
  { path: '/ai-watch', label: 'AI Watch', icon: FiEye, tone: 'purple', roles: ['admin'], permission: 'tab_ai_watch', section: 'Admin' },
  { path: '/product-analytics', label: 'Analytics', icon: FiBarChart2, tone: 'pink', roles: ['admin'], permission: 'tab_product_analytics', section: 'Admin' },
  { path: '/register', label: 'Register', icon: FiUsers, tone: 'teal', roles: ['admin', 'consultant'], permission: 'tab_register', section: 'Admin' },
];

const LayoutShell = ({ children }) => {
  const { user, logout, impersonation, isImpersonating, stopImpersonating } = useAuth();
  const {
    hasPermission,
    hasAnyPermission,
    loading: permissionsLoading,
    usedFallback: permissionsUnavailable,
  } = usePermissions();
  const { overrideTitle } = usePageChrome();
  const { anyMatching } = useAtsMatchRun();
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  useProductAnalyticsTracking();
  useAutoHideScrollbar();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [exitingImpersonation, setExitingImpersonation] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    try {
      return localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === '1';
    } catch {
      return false;
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(SIDEBAR_COLLAPSED_KEY, sidebarCollapsed ? '1' : '0');
    } catch {
      /* ignore */
    }
  }, [sidebarCollapsed]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleStopImpersonating = async () => {
    setExitingImpersonation(true);
    try {
      await stopImpersonating();
      queryClient.clear();
      navigate('/');
    } catch (err) {
      console.error('Failed to stop impersonation:', err);
      void appAlert(err?.response?.data?.error || 'Failed to return to admin session');
    } finally {
      setExitingImpersonation(false);
    }
  };

  const filteredMenuItems = useMemo(
    () =>
      MENU_ITEMS.filter((item) => {
        const role = user?.role;
        if (!role) return false;

        const roles = item.roles || [];
        const candidateOnly = roles.length === 1 && roles[0] === 'candidate';
        const adminOnly = roles.length > 0 && roles.every((r) => r === 'admin');

        // Candidates only see candidate-allowed items
        if (role === 'candidate') {
          if (!roles.includes('candidate')) return false;
        } else if (candidateOnly) {
          return false;
        } else if (adminOnly && role !== 'admin') {
          // Admin-only chrome stays admin-gated (Metadata, scanner, …)
          return false;
        }

        if (permissionsLoading) {
          return roles.includes(role) || (!adminOnly && !candidateOnly && role !== 'candidate');
        }

        if (item.anyOf?.length) return hasAnyPermission(item.anyOf);
        if (item.permission) return hasPermission(item.permission);
        return roles.includes(role);
      }),
    [user?.role, permissionsLoading, hasPermission, hasAnyPermission]
  );

  const menuSections = useMemo(() => {
    const order = [];
    const map = new Map();
    for (const item of filteredMenuItems) {
      const key = item.section || '';
      if (!map.has(key)) {
        map.set(key, []);
        order.push(key);
      }
      map.get(key).push(item);
    }
    return order.map((name) => ({ name, items: map.get(name) }));
  }, [filteredMenuItems]);

  const activeMenuPath = useMemo(
    () => resolveActiveMenuPath(filteredMenuItems, location.pathname, location.search),
    [filteredMenuItems, location.pathname, location.search]
  );

  const pageTitle = useMemo(() => {
    if (overrideTitle) return overrideTitle;
    const params = new URLSearchParams(location.search);
    if (location.pathname === '/crm' && params.get('tab') === 'pipeline') {
      return 'Sales pipeline';
    }
    return titleFromPath(location.pathname, MENU_ITEMS);
  }, [overrideTitle, location.pathname, location.search]);

  useEffect(() => {
    document.title = `${pageTitle} · ${PRODUCT_DISPLAY_NAME}`;
  }, [pageTitle]);

  return (
    <div className={`layout layout--top-title ${sidebarCollapsed ? 'sidebar-is-collapsed' : ''}`}>
      <div className="layout-shell">
        <AnimatePresence>
          {sidebarOpen && (
            <motion.div
              key="sidebar-backdrop"
              className="sidebar-backdrop"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              onClick={() => setSidebarOpen(false)}
              aria-hidden="true"
            />
          )}
        </AnimatePresence>
        <aside className={`sidebar layer-mid ${sidebarOpen ? 'open' : ''} ${sidebarCollapsed ? 'collapsed' : ''}`}>
          <div className="sidebar-brand-row">
            <Link
              to="/"
              className="sidebar-brand-link"
              onClick={() => setSidebarOpen(false)}
              aria-label="Go to dashboard home"
              title="Dashboard home"
            >
              <BrandLogo variant={sidebarCollapsed ? 'compact' : 'navbar'} />
            </Link>
            <button
              type="button"
              className="sidebar-collapse-toggle"
              onClick={() => setSidebarCollapsed((c) => !c)}
              aria-label={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
              title={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            >
              {sidebarCollapsed ? <FiChevronsRight /> : <FiChevronsLeft />}
            </button>
          </div>
          <nav className="sidebar-nav" aria-label="Primary">
            {menuSections.map((section) => (
              <div
                key={section.name || 'main'}
                className="sidebar-section"
                role="group"
                aria-label={section.name || undefined}
              >
                {section.name && !sidebarCollapsed ? (
                  <div className="sidebar-section-label">{section.name}</div>
                ) : null}
                <div className="sidebar-section-items">
                  {section.items.map((item, index) => {
                    const Icon = item.icon;
                    const isActive = item.path === activeMenuPath;
                    return (
                      <MotionSidebarItem
                        key={item.path}
                        className="sidebar-item-wrap"
                        isActive={isActive}
                        delay={index * 0.03}
                      >
                        <Link
                          to={item.path}
                          className={`sidebar-item ${isActive ? 'active' : ''}`}
                          title={item.label}
                          aria-label={item.label}
                          aria-current={isActive ? 'page' : undefined}
                          onClick={() => setSidebarOpen(false)}
                        >
                          <span className={`nav-glyph nav-glyph--${item.tone || 'blue'}`} aria-hidden="true">
                            <Icon />
                          </span>
                          <span className="sidebar-item-label">{item.label}</span>
                        </Link>
                      </MotionSidebarItem>
                    );
                  })}
                </div>
              </div>
            ))}
          </nav>
        </aside>

        <div className="layout-main">
          {isImpersonating && (
            <div className="impersonation-banner" role="status">
              <span>
                Viewing as <strong>{displayName(user)}</strong>
                {impersonation?.impersonator && (
                  <>
                    {' '}
                    (admin: {displayName(impersonation.impersonator)})
                  </>
                )}
              </span>
              <button
                type="button"
                className="impersonation-banner-exit"
                onClick={handleStopImpersonating}
                disabled={exitingImpersonation}
              >
                {exitingImpersonation ? 'Returning…' : 'Return to admin'}
              </button>
            </div>
          )}
          <nav className="navbar layer-front" aria-label="Top bar">
            <div className="navbar-content">
              <button
                type="button"
                className="menu-toggle"
                onClick={() => setSidebarOpen(!sidebarOpen)}
                aria-label={sidebarOpen ? 'Close menu' : 'Open menu'}
              >
                {sidebarOpen ? <FiX /> : <FiMenu />}
              </button>
              <Link
                to="/"
                className="navbar-brand"
                onClick={() => setSidebarOpen(false)}
                aria-label={`${PRODUCT_DISPLAY_NAME} by GoBunnyy home`}
              >
                <BrandLogo variant="navbar" decorative />
              </Link>
              <h1 className="navbar-page-title">{pageTitle}</h1>
              <div className="navbar-trailing">
                <NotificationBell />
                <UserMenu onLogout={handleLogout} />
              </div>
            </div>
          </nav>

          <main className="main-content layer-recess scroll-auto-hide">
            {permissionsUnavailable ? (
              <div className="layout-permissions-banner" role="status">
                Permissions could not be loaded — showing default access for your role. Some actions
                may be rejected by the server until the connection recovers.
              </div>
            ) : null}
            {anyMatching && location.pathname !== '/matches' ? (
              <AiMatchProcessingBar
                matching
                label="ATS match is running in the background. You can keep browsing — scores will update when it finishes."
              />
            ) : null}
            <div className="page-stage">
              <PageTransition>{children}</PageTransition>
            </div>
            <div
              className="layout-build-stamp"
              title="If this version does not change after deploy, the browser or CDN is still serving an old index.html or bundle."
            >
              UI {APP_UI_VERSION}
              {process.env.REACT_APP_BUILD_REF ? ` · ${process.env.REACT_APP_BUILD_REF}` : ''}
            </div>
          </main>
        </div>
      </div>
    </div>
  );
};

const Layout = ({ children }) => (
  <PageChromeProvider>
    <AtsMatchRunProvider>
      <LayoutShell>{children}</LayoutShell>
    </AtsMatchRunProvider>
  </PageChromeProvider>
);

export default Layout;
